import React, { useMemo, useEffect } from "react";
import { useApp } from "../../contexts/AppContext";
import MallMap from "../MallMap/MallMap";
import styles from "./TabContent.module.css";
import ShopTabContent from "./ShopTabContent";
import EventTabContent from "./EventTabContent";

interface TabContentProps {
  activeTabId: string | null;
  tabName: string;
  onTabChange?: (tabId: string | null) => void;
}

export default function TabContent({
  activeTabId,
  tabName,
  onTabChange,
}: TabContentProps) {
  const { state, setSelectedShop, setSelectedEvent } = useApp();

  // Get current tab
  const currentTab = state.tabs?.find((t) => t.id === activeTabId);
  const tabIdNumber = currentTab ? parseInt(currentTab.id) : null;

  // Detect if current tab is the Events tab by name
  const isEventsTab = useMemo(() => {
    const nameRu = currentTab?.name?.ru?.toLowerCase?.() || "";
    const nameEn = currentTab?.name?.en?.toLowerCase?.() || "";
    return nameRu.includes("событ") || nameEn.includes("event");
  }, [currentTab?.name?.ru, currentTab?.name?.en]);

  // Reset selected event when switching away from events tab
  useEffect(() => {
    if (!isEventsTab && state.selectedEvent) {
      setSelectedEvent(undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEventsTab, activeTabId]);

  // Show map for tab "Карта ТРЦ" (id=1)
  if (tabIdNumber === 1) {
    return (
      <>
        <div className={styles.mapWrapper}>
          <div className={styles.headerSection}>
            <h2 className={styles.contentTitle}>{tabName}</h2>
          </div>
          <MallMap
            onShopClick={(shop) => setSelectedShop(shop)}
            onClearSelection={() => setSelectedShop(undefined)}
            selectedShop={state.selectedShop}
          />
          <div className={styles.legendWrapper}>
            <div>
              <div className={styles.legendWrapper}>
                <div className={styles.legendItem}>
                  <svg
                    width="57"
                    height="58"
                    viewBox="0 0 57 58"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M8.15723 56.7229V46.8295H17.7224V36.9361H27.2876V27.0427H36.8529V17.1493H46.4181V7.25586H55.9833"
                      stroke="#2F2B2B"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M0.347778 28.5334C-0.0666633 28.8985 -0.118057 29.54 0.232986 29.9664C0.584029 30.3928 1.20458 30.4425 1.61902 30.0774L0.983398 29.3054L0.347778 28.5334ZM33.2784 1.80145C33.3233 1.24184 32.9208 0.753034 32.3795 0.709672L23.5589 0.00306809C23.0176 -0.0402917 22.5425 0.37821 22.4976 0.937822C22.4528 1.49743 22.8552 1.98624 23.3965 2.0296L31.2371 2.65769L30.5877 10.7638C30.5429 11.3234 30.9454 11.8122 31.4867 11.8556C32.0279 11.8989 32.5031 11.4804 32.5479 10.9208L33.2784 1.80145ZM0.983398 29.3054L1.61902 30.0774L32.934 2.49494L32.2984 1.72294L31.6627 0.950935L0.347778 28.5334L0.983398 29.3054Z"
                      fill="#2F2B2B"
                    />
                  </svg>
                  <span>{state.language === "en" ? "stairs" : "лестница"}</span>
                </div>
                <div className={styles.legendItem}>
                  <svg
                    width="57"
                    height="56"
                    viewBox="0 0 57 56"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <circle
                      cx="25.022"
                      cy="8"
                      r="4"
                      stroke="#2F2B2B"
                      strokeWidth="2"
                    />
                    <rect
                      x="20.022"
                      y="15"
                      width="10"
                      height="20"
                      rx="1.83333"
                      stroke="#2F2B2B"
                      strokeWidth="2"
                    />
                    <path
                      d="M6.52203 52.1659H19.2341C20.3128 52.1659 21.3401 51.7047 22.057 50.8987L45.1075 24.98H51.0776C53.6856 24.98 55.7998 22.8658 55.7998 20.2578V15.3326C55.7998 12.7246 53.6856 10.6104 51.0776 10.6104H40.4198C39.3097 10.6104 38.2558 11.0986 37.538 11.9454L15.2968 38.1846H6.52203C3.91402 38.1846 1.7998 40.2988 1.7998 42.9068V47.4437C1.7998 50.0517 3.91401 52.1659 6.52203 52.1659Z"
                      fill="white"
                      stroke="#2F2B2B"
                      strokeWidth="2"
                    />
                  </svg>
                  <span>
                    {state.language === "en" ? "escalator" : "эскалатор"}
                  </span>
                </div>
                <div className={styles.legendItem}>
                  <svg
                    width="56"
                    height="56"
                    viewBox="0 0 56 56"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M49.2766 3H6.7234C5.7359 3 4.78883 3.39229 4.09056 4.09056C3.39229 4.78883 3 5.7359 3 6.7234V49.2766C3 50.2641 3.39229 51.2112 4.09056 51.9094C4.78883 52.6077 5.7359 53 6.7234 53H49.2766C50.2641 53 51.2112 52.6077 51.9094 51.9094C52.6077 51.2112 53 50.2641 53 49.2766V6.7234C53 5.7359 52.6077 4.78883 51.9094 4.09056C51.2112 3.39229 50.2641 3 49.2766 3ZM41.2979 13.2128V49.8085H29.5957V13.2128H41.2979ZM26.4043 49.8085H14.7021V13.2128H26.4043V49.8085ZM49.8085 49.2766C49.8085 49.4177 49.7525 49.553 49.6527 49.6527C49.553 49.7525 49.4177 49.8085 49.2766 49.8085H44.4894V11.617C44.4894 11.1938 44.3212 10.7879 44.022 10.4887C43.7227 10.1894 43.3168 10.0213 42.8936 10.0213H13.1064C12.6832 10.0213 12.2773 10.1894 11.978 10.4887C11.6788 10.7879 11.5106 11.1938 11.5106 11.617V49.8085H6.7234C6.58233 49.8085 6.44704 49.7525 6.34728 49.6527C6.24753 49.553 6.19149 49.4177 6.19149 49.2766V6.7234C6.19149 6.58233 6.24753 6.44704 6.34728 6.34728C6.44704 6.24753 6.58233 6.19149 6.7234 6.19149H49.2766C49.4177 6.19149 49.553 6.24753 49.6527 6.34728C49.7525 6.44704 49.8085 6.58233 49.8085 6.7234V49.2766Z"
                      fill="#2F2B2B"
                      stroke="white"
                    />
                    <path
                      d="M23.0366 33.875H17.7121C17.3805 33.8761 17.1381 33.7295 16.985 33.4353C16.8319 33.141 16.8386 32.854 17.005 32.5743L19.6672 28.2946C19.8336 28.0149 20.0693 27.875 20.3744 27.875C20.6794 27.875 20.9151 28.0149 21.0815 28.2946L23.7437 32.5743C23.9112 32.8551 23.9184 33.1421 23.7654 33.4353C23.6123 33.7284 23.3694 33.875 23.0366 33.875Z"
                      stroke="#2F2B2B"
                      strokeWidth="1.5"
                    />
                    <path
                      d="M38.7445 29.1757L36.0817 33.4554C35.9152 33.7351 35.6795 33.875 35.3744 33.875C35.0692 33.875 34.8335 33.7351 34.667 33.4554L32.0042 29.1757C31.8389 28.8971 31.8322 28.6107 31.9842 28.3164C32.1362 28.0221 32.3787 27.875 32.7115 27.875H38.0372C38.3712 27.8739 38.6142 28.0205 38.7662 28.3147C38.9182 28.609 38.911 28.896 38.7445 29.1757Z"
                      stroke="#2F2B2B"
                      strokeWidth="1.5"
                    />
                  </svg>
                  <span>{state.language === "en" ? "elevator" : "лифт"}</span>
                </div>
                <div className={styles.legendItem}>
                  <svg
                    width="56"
                    height="56"
                    viewBox="0 0 56 56"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M28 4.80811L44.2617 21.0708C45.4802 22.2897 46.165 23.943 46.165 25.6665C46.165 27.3901 45.4803 29.0433 44.2617 30.2622C41.8973 32.6287 37.8012 32.7266 35.334 30.5122L34.5 29.7632V42.0005C34.4998 45.5835 31.583 48.5005 28 48.5005C24.417 48.5004 21.5002 45.5834 21.5 42.0005V29.7651L20.666 30.5112C18.1964 32.7237 14.1066 32.6272 11.7373 30.2622H11.7383C10.5197 29.0433 9.83496 27.3901 9.83496 25.6665C9.83502 23.943 10.5198 22.2897 11.7383 21.0708L28 4.80811ZM14.3301 23.6636C13.799 24.1948 13.501 24.9153 13.501 25.6665C13.501 26.4177 13.7991 27.1381 14.3301 27.6694V27.6704L14.3379 27.6782C14.875 28.1918 15.5899 28.4779 16.333 28.478C17.0763 28.478 17.7919 28.1919 18.3291 27.6782L18.3369 27.6704L25.167 20.8394V42.022C25.1996 42.7512 25.5125 43.4402 26.04 43.9448C26.5677 44.4494 27.2699 44.7309 28 44.731C28.7301 44.731 29.4323 44.4494 29.96 43.9448C30.4876 43.4402 30.8004 42.7513 30.833 42.022V20.8394L37.6631 27.6694V27.6704L37.6719 27.6782C38.2091 28.1917 38.9239 28.478 39.667 28.478C40.4101 28.478 41.1249 28.1918 41.6621 27.6782L41.6699 27.6694C42.201 27.1381 42.5 26.4177 42.5 25.6665C42.4999 24.9153 42.201 24.1949 41.6699 23.6636L28 9.99365L14.3301 23.6636Z"
                      fill="#2F2B2B"
                      stroke="white"
                    />
                  </svg>
                  <span>{state.language === "en" ? "entrance" : "вход"}</span>
                </div>
                <div className={styles.legendItem}>
                  <svg
                    width="56"
                    height="56"
                    viewBox="0 0 56 56"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M11.9321 41.1869C11.6984 41.8459 10.7678 41.851 10.5268 41.1946L3.7356 22.6926C3.55731 22.2069 3.91491 21.6917 4.43224 21.6888L17.8119 21.6153C18.3293 21.6124 18.6925 22.1237 18.5196 22.6114L11.9321 41.1869Z"
                      stroke="#2F2B2B"
                      strokeWidth="2.14286"
                    />
                    <circle
                      cx="44.4714"
                      cy="13.9738"
                      r="4.39286"
                      stroke="#2F2B2B"
                      strokeWidth="1.92857"
                    />
                    <path
                      d="M43.7686 22.4868C44.0059 21.8291 44.9365 21.8291 45.1738 22.4868L51.8633 41.0259C52.0389 41.5125 51.6785 42.0258 51.1611 42.0259H37.7812C37.2638 42.0259 36.9035 41.5126 37.0791 41.0259L43.7686 22.4868Z"
                      stroke="#2F2B2B"
                      strokeWidth="2.14286"
                    />
                    <circle
                      cx="11.2575"
                      cy="13.9738"
                      r="4.39286"
                      stroke="#2F2B2B"
                      strokeWidth="1.92857"
                    />
                    <path
                      d="M28.0752 5.33105H29.7959C30.2477 5.33111 30.6143 5.69758 30.6143 6.14941V47.5127C30.6143 47.9645 30.2477 48.331 29.7959 48.3311H28.0752C27.6233 48.3311 27.2568 47.9646 27.2568 47.5127V6.14941C27.2568 5.69755 27.6233 5.33105 28.0752 5.33105Z"
                      stroke="#2F2B2B"
                      strokeWidth="2"
                      strokeLinejoin="round"
                    />
                  </svg>
                  <span>{state.language === "en" ? "WC" : "туалет"}</span>
                </div>
                <div className={styles.legendItem}>
                  <svg
                    width="40"
                    height="52"
                    viewBox="0 0 40 52"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M8.75098 5.09453C8.75098 2.83343 10.5687 1 12.8111 1C15.0538 1 16.8708 2.83343 16.8708 5.09453C16.8708 7.35749 15.0538 9.19128 12.8111 9.19128C10.5687 9.19128 8.75098 7.35749 8.75098 5.09453Z"
                      stroke="#2F2B2B"
                      strokeWidth="2"
                    />
                    <path
                      d="M38.2124 40.336L31.9666 27.4798C31.9205 27.3518 31.7438 27.0368 31.7408 27.0398C31.3974 26.5534 30.8345 26.2359 30.1934 26.2359L30.1844 26.2366L30.1852 26.2329L21.5497 27.0582L20.9131 23.2651L28.1487 22.5634C29.4182 22.4064 29.8316 21.8248 29.8609 20.8894C29.8916 19.9341 29.1486 19.1361 28.2031 19.1043L20.3308 19.7862L19.4702 14.6518C19.1122 12.512 17.099 11.068 14.9791 11.4305C12.8562 11.7926 11.4269 13.8212 11.7856 15.9624L13.9467 28.8594C14.6406 31.1755 16.1977 31.911 19.6335 31.6635L28.7183 30.7962L34.2936 42.2736C34.6726 43.0525 35.4484 43.5071 36.2554 43.5071C36.5775 43.5071 36.9044 43.434 37.213 43.2805C38.2956 42.7469 38.7412 41.4276 38.2124 40.336Z"
                      stroke="#2F2B2B"
                      strokeWidth="2"
                      strokeLinejoin="round"
                    />
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M16.5524 47.5562C9.85146 47.5562 4.39984 42.0342 4.39984 35.2498C4.39984 31.0246 6.51042 27.2936 9.71932 25.0775L9.11152 21.4241C4.28377 24.1008 1 29.2919 1 35.2498C1 43.9339 7.9772 50.9999 16.5524 50.9999C22.3489 50.9999 27.4137 47.7693 30.0875 42.9913L28.139 38.9432C26.5861 43.9279 21.9819 47.5562 16.5524 47.5562Z"
                      stroke="#2F2B2B"
                      strokeWidth="2"
                      strokeLinejoin="round"
                    />
                  </svg>
                  <span>
                    {state.language === "en" ? "WC" : "туалет для инвалидов"}
                  </span>
                </div>
                <div className={styles.legendItem}>
                  <svg
                    width="56"
                    height="56"
                    viewBox="0 0 56 56"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M14.12 4.00001C12.152 4.00001 10.484 5.47601 10.28 7.45001C9.984 10.266 9.5 15.364 9.5 18.5C9.5 21.46 10.856 24.104 12.974 25.844C13.67 26.416 14 27.044 14 27.572V28.896L13.998 28.99C13.916 30.188 13 43.564 13 46C13 46.7879 13.1552 47.5682 13.4567 48.2961C13.7583 49.0241 14.2002 49.6855 14.7574 50.2426C15.3145 50.7998 15.9759 51.2418 16.7039 51.5433C17.4319 51.8448 18.2121 52 19 52C19.7879 52 20.5681 51.8448 21.2961 51.5433C22.0241 51.2418 22.6855 50.7998 23.2426 50.2426C23.7998 49.6855 24.2417 49.0241 24.5433 48.2961C24.8448 47.5682 25 46.7879 25 46C25 43.564 24.086 30.188 24.002 28.99L24 28.896V27.572C24 27.042 24.33 26.416 25.026 25.844C26.1131 24.9538 26.9888 23.8331 27.5896 22.563C28.1904 21.2929 28.5014 19.9051 28.5 18.5C28.5 15.364 28.016 10.266 27.72 7.45001C27.6201 6.50182 27.1722 5.62433 26.4629 4.98712C25.7537 4.34991 24.8334 3.99822 23.88 4.00001C22.966 4.00001 22.124 4.31601 21.46 4.84601C20.7572 4.29779 19.8914 4.00004 19 4.00001C18.1086 4.00004 17.2428 4.29779 16.54 4.84601C15.8533 4.29655 14.9995 3.99808 14.12 4.00001ZM18 8.00001C18 7.73479 18.1054 7.48044 18.2929 7.2929C18.4804 7.10537 18.7348 7.00001 19 7.00001C19.2652 7.00001 19.5196 7.10537 19.7071 7.2929C19.8946 7.48044 20 7.73479 20 8.00001V18.5C20 18.8978 20.158 19.2794 20.4393 19.5607C20.7206 19.842 21.1022 20 21.5 20C21.8978 20 22.2794 19.842 22.5607 19.5607C22.842 19.2794 23 18.8978 23 18.5V7.88001C23 7.64662 23.0927 7.42279 23.2577 7.25776C23.4228 7.09272 23.6466 7.00001 23.88 7.00001C24.33 7.00001 24.694 7.33401 24.738 7.76201C25.034 10.602 25.5 15.546 25.5 18.5C25.5014 19.4617 25.2888 20.4117 24.8775 21.281C24.4662 22.1503 23.8665 22.9172 23.122 23.526C22.046 24.41 21 25.806 21 27.572V28.896C21 28.996 21.002 29.088 21.01 29.196C21.098 30.486 22 43.698 22 46C22 46.7957 21.6839 47.5587 21.1213 48.1213C20.5587 48.6839 19.7956 49 19 49C18.2044 49 17.4413 48.6839 16.8787 48.1213C16.3161 47.5587 16 46.7957 16 46C16 43.7 16.9 30.486 16.99 29.196C16.998 29.088 17 28.996 17 28.896V27.572C17 25.804 15.954 24.41 14.878 23.526C14.1335 22.9172 13.5338 22.1503 13.1225 21.281C12.7112 20.4117 12.4986 19.4617 12.5 18.5C12.5 15.546 12.966 10.604 13.262 7.76201C13.2848 7.55112 13.3853 7.35628 13.5439 7.21543C13.7025 7.07457 13.9079 6.99778 14.12 7.00001C14.3534 7.00001 14.5772 7.09272 14.7423 7.25776C14.9073 7.42279 15 7.64662 15 7.88001V18.5C15 18.8978 15.158 19.2794 15.4393 19.5607C15.7206 19.842 16.1022 20 16.5 20C16.8978 20 17.2794 19.842 17.5607 19.5607C17.842 19.2794 18 18.8978 18 18.5V8.00001ZM45 7.01201V26.5C45 27.2 45.188 30.494 45.396 34.12L45.408 34.33C45.688 39.202 46 44.66 46 46C46 46.7957 45.6839 47.5587 45.1213 48.1213C44.5587 48.6839 43.7957 49 43 49C42.2043 49 41.4413 48.6839 40.8787 48.1213C40.3161 47.5587 40 46.7957 40 46C40 44.81 40.246 39.994 40.498 35.4C40.6455 32.7078 40.7975 30.0158 40.954 27.324L40.998 26.588L41 26.5C41 26.1022 40.842 25.7207 40.5607 25.4394C40.2794 25.158 39.8978 25 39.5 25H35.5C35.3674 25 35.2402 24.9473 35.1464 24.8536C35.0527 24.7598 35 24.6326 35 24.5V17.5C35 14.8018 36.0387 12.2072 37.9007 10.2544C39.7626 8.30157 42.3049 7.14049 45 7.01201ZM37.91 28L37.846 29.112C37.752 30.746 37.626 32.952 37.502 35.236C37.254 39.77 37 44.704 37 46C37 46.7879 37.1552 47.5682 37.4567 48.2961C37.7583 49.0241 38.2002 49.6855 38.7574 50.2426C39.3145 50.7998 39.9759 51.2418 40.7039 51.5433C41.4319 51.8448 42.2121 52 43 52C43.7879 52 44.5681 51.8448 45.2961 51.5433C46.0241 51.2418 46.6855 50.7998 47.2426 50.2426C47.7998 49.6855 48.2417 49.0241 48.5433 48.2961C48.8448 47.5682 49 46.7879 49 46C49 44.558 48.684 39.052 48.412 34.29L48.404 34.158C48.186 30.36 48 27.1 48 26.5V5.50001C48 5.10218 47.842 4.72065 47.5607 4.43935C47.2794 4.15804 46.8978 4.00001 46.5 4.00001H45.5C41.9196 4.00001 38.4858 5.42233 35.9541 7.95407C33.4223 10.4858 32 13.9196 32 17.5V24.5C32 26.432 33.568 28 35.5 28H37.91Z"
                      fill="#2F2B2B"
                      stroke="white"
                    />
                  </svg>
                  <span>{state.language === "en" ? "cafe" : "кафе"}</span>
                </div>
                <div className={styles.legendItem}>
                  <svg
                    width="42"
                    height="54"
                    viewBox="0 0 42 54"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M1 33.4262L5.50746 33.4489L5.50465 49.6053C5.49787 53.4253 9.78412 53.4855 9.78452 49.6028C9.78452 47.6523 9.67595 34.2824 9.82725 33.4569L15.377 33.4501C15.0884 32.1307 14.5767 31.6637 14.1806 30.3407C13.7715 28.9718 13.375 27.6237 12.9646 26.2324C12.69 25.3027 12.9135 25.1721 13.2149 24.3062C13.4476 23.6376 13.6684 22.9845 13.8984 22.327C14.1607 21.5769 14.9255 18.859 15.3231 18.449C15.8433 19.1616 16.7595 20.0375 17.508 20.691C18.7715 21.794 20.3591 24.6357 22.0936 22.8093C23.7092 21.1083 21.247 19.5305 19.9847 18.2163C18.7986 16.9816 14.2696 12.1292 13.0038 11.6645C11.7686 11.2114 10.8013 10.8789 9.3981 11.3967C7.0815 12.251 6.6727 14.6007 5.94176 16.9277C4.90741 20.2204 3.48069 23.9509 2.70264 27.2109L1 33.4262Z"
                      stroke="#2F2B2B"
                      strokeWidth="2"
                      strokeLinejoin="round"
                    />
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M19.3767 29.0239C19.6637 31.8599 18.692 31.0248 18.7994 32.9486C18.8728 34.2687 19.8114 35.2951 20.9172 35.5877C21.4917 35.7398 29.7062 35.7549 30.3916 35.6156C32.2332 35.2411 33.305 32.9282 32.2172 31.1613C31.6519 30.2435 31.5405 30.81 31.5541 29.4184C31.5644 28.3557 31.9162 25.9616 29.9685 26.1568C28.3892 26.3153 28.6786 28.2335 28.6929 29.7069L22.2641 29.766C22.2027 25.744 22.3956 26.1568 18.2066 26.1381C17.4493 26.1397 16.9419 26.2586 16.6157 26.7117C16.2724 27.1892 16.2568 27.875 16.5878 28.3744C17.2329 29.3489 17.9918 28.9377 19.3767 29.0239Z"
                      stroke="#2F2B2B"
                      strokeWidth="2"
                      strokeLinejoin="round"
                    />
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M16.2207 39.0286L39.6518 39.0338L39.6634 35.9488L16.21 35.9072L16.2207 39.0286Z"
                      stroke="#2F2B2B"
                      strokeWidth="2"
                      strokeLinejoin="round"
                    />
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M15.838 1.05555C13.6834 1.38928 11.8139 3.46078 12.2056 6.03208C13.0579 11.6262 21.6983 10.5387 20.8536 4.69953C20.5359 2.50428 18.428 0.654343 15.838 1.05555Z"
                      stroke="#2F2B2B"
                      strokeWidth="2"
                      strokeLinejoin="round"
                    />
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M37.0942 29.941C33.447 30.9981 35.0913 36.5275 38.7313 35.4049C40.004 35.0125 41.1309 33.5594 40.6227 31.8488C40.2435 30.5717 38.8291 29.4384 37.0942 29.941Z"
                      stroke="#2F2B2B"
                      strokeWidth="2"
                      strokeLinejoin="round"
                    />
                  </svg>
                  <span>
                    {state.language === "en"
                      ? "mother and child room"
                      : "комната матери и ребенка"}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  // Render Events tab content
  if (isEventsTab) {
    return (
      <EventTabContent
        activeTabId={activeTabId}
        tabName={tabName}
        onTabChange={onTabChange}
        tabIdNumber={tabIdNumber}
      />
    );
  }

  // Render Shops tab content
  return (
    <ShopTabContent
      activeTabId={activeTabId}
      tabName={tabName}
      onTabChange={onTabChange}
      tabIdNumber={tabIdNumber}
      currentTab={currentTab}
    />
  );
}
